import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, Col, Empty, Input, Row, Typography } from "antd";
import { ArrowRight, Boxes, Grid3X3, PackageSearch, Search } from "lucide-react";
import { api } from "../services/api";
import CmsEditableText from "../components/cms/CmsEditableText";
import CmsEditBar from "../components/cms/CmsEditBar";
import { useCmsPage } from "../hooks/useCmsPage";
import { resolveProductImage } from "../utils/productAssets";
import "./CategoriesPage.css";

const { Title, Text } = Typography;

const MAX_VISIBLE_SUBCATEGORIES = 6;
const SKELETON_COUNT = 9;
const numberFormatter = new Intl.NumberFormat();

function categoryLink(category) {
  return `/all-products?categorySlug=${encodeURIComponent(category.slug)}`;
}

function subcategoryLink(category, subcategory) {
  return `/all-products?categorySlug=${encodeURIComponent(category.slug)}&subcategorySlug=${encodeURIComponent(subcategory.slug)}`;
}

function formatProductCount(count) {
  const value = Number(count) || 0;
  return `${numberFormatter.format(value)} product${value === 1 ? "" : "s"}`;
}

function totalSubcategories(categories = []) {
  return categories.reduce((sum, category) => sum + (category.subcategories?.length || 0), 0);
}

function totalProducts(categories = []) {
  return categories.reduce((sum, category) => sum + (Number(category.count) || 0), 0);
}

function CategorySkeletonCard() {
  return (
    <div className="category-card category-card--skeleton" aria-hidden="true">
      <div className="skeleton-block skeleton-block--media" />
      <div className="category-card__content">
        <div className="skeleton-block skeleton-block--title" />
        <div className="skeleton-block skeleton-block--count" />
        <div className="skeleton-block skeleton-block--line" />
        <div className="skeleton-block skeleton-block--line skeleton-block--line-short" />
        <div className="category-card__subgrid">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="skeleton-block skeleton-block--chip" />
          ))}
        </div>
      </div>
    </div>
  );
}

function CategoryCard({ category }) {
  const subcategories = category.subcategories || [];
  const visibleSubcategories = subcategories.slice(0, MAX_VISIBLE_SUBCATEGORIES);
  const hiddenCount = subcategories.length - visibleSubcategories.length;
  const browseLabel = `Browse ${category.name}`;

  return (
    <Card bordered={false} className="category-card" bodyStyle={{ padding: 0, height: "100%" }}>
      <div className="category-card__content">
        <div className="category-card__heading">
          <Link to={categoryLink(category)} className="category-card__image-link" aria-label={browseLabel}>
            <img
              src={resolveProductImage(category.image)}
              alt={category.name}
              className="category-card__image"
              loading="lazy"
            />
          </Link>
          <div className="category-card__meta">
            <Link to={categoryLink(category)} className="category-card__name">
              <Title level={4} className="category-card__title">
                {category.name}
              </Title>
            </Link>
            <div className="category-card__facts">
              <span>{formatProductCount(category.count)}</span>
              <span>{subcategories.length} subcategor{subcategories.length === 1 ? "y" : "ies"}</span>
            </div>
          </div>
          <Link to={categoryLink(category)} className="category-card__icon-link" aria-label={browseLabel}>
            <ArrowRight size={17} />
          </Link>
        </div>

        {category.description ? (
          <Text className="category-card__description">{category.description}</Text>
        ) : (
          <Text className="category-card__description category-card__description--muted">
            Machine parts, attachments, and replacement inventory.
          </Text>
        )}

        <div className="category-card__sub-header">
          <span className="category-card__sub-label">Subcategories</span>
          <Link to={categoryLink(category)} className="category-card__view-all">
            View all
          </Link>
        </div>

        {subcategories.length ? (
          <div className="category-card__subgrid">
            {visibleSubcategories.map((subcategory) => (
              <Link
                key={subcategory.slug || subcategory.name}
                to={subcategoryLink(category, subcategory)}
                className="category-card__subtile"
                title={subcategory.name}
              >
                <span className="category-card__subtile-image">
                  <img
                    src={resolveProductImage(subcategory.image)}
                    alt=""
                    loading="lazy"
                />
                </span>
                <span className="category-card__subtile-copy">
                  <span className="category-card__subtile-name">{subcategory.name}</span>
                  {subcategory.description && (
                    <span className="category-card__subtile-description">{subcategory.description}</span>
                  )}
                </span>
              </Link>
            ))}
            {hiddenCount > 0 && (
              <Link to={categoryLink(category)} className="category-card__subtile category-card__subtile--more">
                +{hiddenCount} more
              </Link>
            )}
          </div>
        ) : (
          <Text className="category-card__empty-subs">Browse all products in this department.</Text>
        )}
      </div>
    </Card>
  );
}

function CatalogStat({ icon: Icon, label, value }) {
  return (
    <div className="categories-page__stat">
      <span className="categories-page__stat-icon">
        <Icon size={18} />
      </span>
      <span>
        <strong>{value}</strong>
        <small>{label}</small>
      </span>
    </div>
  );
}

export default function CategoriesPage() {
  const cms = useCmsPage("categories", {
    header: {
      eyebrow: "Bobbin USA catalog",
      headline: "Shop By Category",
      subtitle: "Browse industrial sewing machine parts by department, machine series, and replacement part group.",
    },
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchCategories = useCallback(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    api
      .get("/categories")
      .then((data) => {
        if (mounted) setCategories(data.categories || []);
      })
      .catch(() => {
        if (mounted) setError("We couldn't load categories. Please try again.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const cancel = fetchCategories();
    return cancel;
  }, [fetchCategories]);

  const filteredCategories = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return categories;

    return categories.filter((category) =>
      [
        category.name,
        category.slug,
        category.description,
        ...(category.subcategories || []).map((subcategory) => subcategory.name),
      ]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [categories, searchTerm]);

  const stats = useMemo(() => ({
    categories: categories.length,
    subcategories: totalSubcategories(categories),
    products: totalProducts(categories),
  }), [categories]);

  const showSearch = !loading && !error && categories.length > 0;

  return (
    <main className="categories-page">
      <CmsEditBar editMode={cms.editMode} pageTitle="Categories Page" />
      <div className="categories-page__container">
        <section className="categories-page__header">
          <div>
            <CmsEditableText as={Text} className="categories-page__eyebrow" editMode={cms.editMode} fieldKey="eyebrow" onSave={cms.updateField} sectionKey="header" value={cms.text("header", "eyebrow", "Bobbin USA catalog")} />
            <CmsEditableText as={Title} className="categories-page__title" editMode={cms.editMode} fieldKey="headline" onSave={cms.updateField} sectionKey="header" value={cms.text("header", "headline", "Shop By Category")} />
            <CmsEditableText as={Text} className="categories-page__subtitle" editMode={cms.editMode} fieldKey="subtitle" multiline onSave={cms.updateField} sectionKey="header" value={cms.text("header", "subtitle", "Browse industrial sewing machine parts by department, machine series, and replacement part group.")} />
          </div>
        </section>

        <section className="categories-page__controls">
          <div className="categories-page__control-panel">
            <div className="categories-page__stats">
              <CatalogStat icon={Grid3X3} label="Categories" value={stats.categories} />
              <CatalogStat icon={Boxes} label="Subcategories" value={stats.subcategories} />
              <CatalogStat icon={PackageSearch} label="Products" value={numberFormatter.format(stats.products)} />
            </div>

            {showSearch && (
              <div className="categories-page__search-wrap">
                <Search size={18} aria-hidden="true" />
                <Input
                  allowClear
                  placeholder="Search category, brand, machine series"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="categories-page__search"
                  aria-label="Search categories"
                />
              </div>
            )}
          </div>
        </section>

        <div className="categories-page__toolbar">
          <div>
            <span className="categories-page__section-label">Catalog departments</span>
            <p>{filteredCategories.length} of {categories.length || SKELETON_COUNT} categories shown</p>
          </div>
          {searchTerm && (
            <Button className="categories-page__clear" onClick={() => setSearchTerm("")}>
              Clear search
            </Button>
          )}
        </div>

        {loading ? (
          <Row gutter={[16, 16]}>
            {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <Col key={index} xs={24} lg={12}>
                <CategorySkeletonCard />
              </Col>
            ))}
          </Row>
        ) : error ? (
          <Card bordered={false} className="categories-state-card">
            <Empty description={error} />
            <Button type="primary" className="categories-state-card__action" onClick={fetchCategories}>
              Try again
            </Button>
          </Card>
        ) : categories.length === 0 ? (
          <Card bordered={false} className="categories-state-card">
            <Empty description="No categories are available yet." />
          </Card>
        ) : filteredCategories.length === 0 ? (
          <Card bordered={false} className="categories-state-card">
            <Empty description={`No categories match "${searchTerm}".`} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            <Button className="categories-state-card__action" onClick={() => setSearchTerm("")}>
              Clear search
            </Button>
          </Card>
        ) : (
          <Row gutter={[16, 16]}>
            {filteredCategories.map((category) => (
              <Col key={category.slug || category.name} xs={24} lg={12}>
                <CategoryCard category={category} />
              </Col>
            ))}
          </Row>
        )}
      </div>
    </main>
  );
}
